import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaEye,
  FaFileAlt,
  FaGlobeAmericas,
} from "react-icons/fa";
import TermsCmsEditor from "../../components/cms/TermsCmsEditor";
import CmsPreviewModal from "../../components/cms/CmsPreviewModal";
import {
  TERMS_MODULE_INTRO,
  TERMS_MODULE_LABELS,
  TERMS_MODULE_OPTIONS,
} from "../../constants/cms";
import {
  createTerms,
  createTermsNewVersion,
  fetchTermsHistory,
  updateTerms,
  cmsExtractMessage,
} from "../../services/cms.service";
import {
  getDefaultTermsCmsConfig,
  parseTermsCmsConfig,
  serializeTermsCmsConfig,
  termsContentToDisplayHtml,
} from "../../utils/termsModuleCms";
import "../../assets/styles/super_admin.scss";
import "../../assets/styles/cms_management.scss";
import "../../assets/styles/cms_terms_management.scss";
import "../../assets/styles/cms_contact_editor.scss";

function formatDisplayDate(value) {
  if (!value) return "—";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD MMM YYYY") : value;
}

export default function SuperAdminTermsManagementPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialModule = searchParams.get("module") || "PLATFORM";

  const [moduleName, setModuleName] = useState(initialModule);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    title: "",
    subtitle: "",
    contentTitle: "",
    contentIntro: "",
    content: "",
    effectiveDate: "",
    updatedAt: "",
    updatedBy: "",
    status: "",
  });
  const [form, setForm] = useState({
    effective_date: dayjs().format("YYYY-MM-DD"),
  });
  const [termsConfig, setTermsConfig] = useState(() =>
    getDefaultTermsCmsConfig(initialModule, TERMS_MODULE_LABELS[initialModule] || initialModule),
  );

  const moduleLabel = TERMS_MODULE_LABELS[moduleName] || moduleName;

  const serializedContent = useMemo(
    () => serializeTermsCmsConfig(termsConfig, { moduleName, moduleLabel }),
    [termsConfig, moduleName, moduleLabel],
  );

  const applyRecord = useCallback(
    (record) => {
      if (!record) {
        setSelected(null);
        setForm({ effective_date: dayjs().format("YYYY-MM-DD") });
        setTermsConfig(getDefaultTermsCmsConfig(moduleName, moduleLabel));
        return;
      }
      setSelected(record);
      setForm({
        effective_date: record.effectiveDate || dayjs().format("YYYY-MM-DD"),
      });
      setTermsConfig(
        parseTermsCmsConfig(record.content, {
          moduleName,
          moduleLabel,
        }),
      );
    },
    [moduleName, moduleLabel],
  );

  const loadModuleData = useCallback(async () => {
    setLoading(true);
    try {
      const historyRows = await fetchTermsHistory(moduleName);
      const live =
        historyRows.find((r) => r.status === "ACTIVE") ||
        historyRows.find((r) => r.status === "DRAFT") ||
        historyRows[0] ||
        null;

      if (live) {
        applyRecord(live);
      } else {
        // Bootstrap a live record for this module.
        const created = await createTerms({
          module_name: moduleName,
          content: serializeTermsCmsConfig(
            getDefaultTermsCmsConfig(moduleName, TERMS_MODULE_LABELS[moduleName] || moduleName),
            {
              moduleName,
              moduleLabel: TERMS_MODULE_LABELS[moduleName] || moduleName,
            },
          ),
          effective_date: dayjs().format("YYYY-MM-DD"),
          publish: true,
        });
        applyRecord(created);
      }
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to load terms"));
      applyRecord(null);
    } finally {
      setLoading(false);
    }
  }, [moduleName, applyRecord]);

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("module", moduleName);
      next.delete("version");
      return next;
    });
  }, [moduleName, setSearchParams]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let record = selected;
      if (!record?.id) {
        record = await createTermsNewVersion(moduleName);
      }
      const updated = await updateTerms(record.id, {
        content: serializedContent,
        effective_date: form.effective_date,
        publish: true,
      });
      toast.success("Terms saved");
      applyRecord(updated);
    } catch (err) {
      toast.error(cmsExtractMessage(err, "Failed to save terms"));
    } finally {
      setSaving(false);
    }
  };

  const openPreview = useCallback(() => {
    const content = termsContentToDisplayHtml(serializedContent, {
      moduleName,
      moduleLabel,
    });
    setPreviewModal({
      title: `${moduleLabel} — Terms & Conditions`,
      subtitle: "Preview of the live public terms page content.",
      contentTitle: `${moduleLabel} Terms`,
      contentIntro: TERMS_MODULE_INTRO[moduleName] || TERMS_MODULE_INTRO.PLATFORM,
      content,
      effectiveDate: form.effective_date,
      updatedAt: selected?.updatedAt,
      updatedBy: selected?.updatedBy || selected?.createdBy || "Super Admin",
      status: "ACTIVE",
    });
    setPreviewOpen(true);
  }, [serializedContent, moduleName, moduleLabel, form.effective_date, selected]);

  const publicUrl = useMemo(
    () =>
      moduleName === "PLATFORM"
        ? "/terms-and-conditions"
        : `/terms-and-conditions?module=${moduleName}`,
    [moduleName],
  );

  return (
    <div className="cms-terms-page">
      <div className="cms-terms-page__header">
        <div className="cms-terms-page__header-copy">
          <nav className="cms-terms-page__breadcrumb" aria-label="Breadcrumb">
            <Link to="/super-admin/cms">CMS Management</Link>
            <span aria-hidden>&gt;</span>
            <span>Terms &amp; Conditions</span>
          </nav>
          <h1 className="cms-terms-page__title">Terms &amp; Conditions</h1>
          <p className="cms-terms-page__subtitle">
            Edit accordion sections for each module. Click <strong>Save</strong> to publish changes
            live — no version workflow.
          </p>
        </div>
        <div className="cms-terms-page__header-actions">
          <button
            type="button"
            className="cms-terms-btn cms-terms-btn--outline"
            onClick={openPreview}
            disabled={loading || saving}
          >
            <FaEye aria-hidden />
            Preview
          </button>
          <a
            className="cms-terms-btn cms-terms-btn--outline"
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGlobeAmericas aria-hidden />
            Open live
          </a>
          <button
            type="button"
            className="cms-terms-btn cms-terms-btn--primary"
            onClick={handleSave}
            disabled={loading || saving}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="cms-terms-stats">
        <article className="cms-terms-stat-card">
          <span className="cms-terms-stat-card__icon cms-terms-stat-card__icon--orange" aria-hidden>
            <FaFileAlt />
          </span>
          <div>
            <p className="cms-terms-stat-card__label">Module</p>
            <p className="cms-terms-stat-card__value">{moduleLabel}</p>
            <p className="cms-terms-stat-card__hint">Public page module</p>
          </div>
        </article>
        <article className="cms-terms-stat-card">
          <span className="cms-terms-stat-card__icon cms-terms-stat-card__icon--blue" aria-hidden>
            <FaCalendarAlt />
          </span>
          <div>
            <p className="cms-terms-stat-card__label">Effective Date</p>
            <p className="cms-terms-stat-card__value">{formatDisplayDate(form.effective_date)}</p>
            <p className="cms-terms-stat-card__hint">{form.effective_date || "—"}</p>
          </div>
        </article>
        <article className="cms-terms-stat-card">
          <span className="cms-terms-stat-card__icon cms-terms-stat-card__icon--green" aria-hidden>
            <FaCheckCircle />
          </span>
          <div>
            <p className="cms-terms-stat-card__label">Status</p>
            <p className="cms-terms-stat-card__value cms-terms-stat-card__value--green">
              {selected?.status === "ACTIVE" ? "Published" : selected ? "Saving publishes live" : "—"}
            </p>
            <p className="cms-terms-stat-card__hint">
              {selected?.updatedAt
                ? `Last saved ${formatDisplayDate(selected.updatedAt)}`
                : "Save to publish"}
            </p>
          </div>
        </article>
      </div>

      <div className="cms-terms-page__module-tabs" role="tablist" aria-label="Terms modules">
        {TERMS_MODULE_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={moduleName === opt.value}
            className={`cms-terms-page__module-tab${
              moduleName === opt.value ? " cms-terms-page__module-tab--active" : ""
            }`}
            onClick={() => setModuleName(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="cms-terms-workspace cms-terms-workspace--simple">
        <section className="cms-terms-editor-card">
          <h2 className="cms-terms-editor-card__title">Configure Terms Sections</h2>
          {loading ? (
            <p className="cms-terms-editor-card__loading">Loading...</p>
          ) : (
            <>
              <div className="cms-terms-editor-card__editor">
                <TermsCmsEditor
                  config={termsConfig}
                  onChange={setTermsConfig}
                  readOnly={false}
                  moduleLabel={moduleLabel}
                  effectiveDate={form.effective_date}
                  onEffectiveDateChange={(value) =>
                    setForm((f) => ({ ...f, effective_date: value }))
                  }
                />
              </div>
              <div className="cms-terms-editor-card__footer">
                <div className="cms-terms-editor-card__actions">
                  <button
                    type="button"
                    className="cms-terms-btn cms-terms-btn--outline"
                    onClick={openPreview}
                    disabled={saving}
                  >
                    <FaEye aria-hidden />
                    Preview
                  </button>
                  <button
                    type="button"
                    className="cms-terms-btn cms-terms-btn--primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      <CmsPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title={previewModal.title || `${moduleLabel} — Terms & Conditions`}
        subtitle={previewModal.subtitle}
        contentTitle={previewModal.contentTitle}
        contentIntro={previewModal.contentIntro}
        content={previewModal.content}
        effectiveDate={previewModal.effectiveDate}
        updatedAt={previewModal.updatedAt}
        updatedBy={previewModal.updatedBy}
        status={previewModal.status}
        liveUrl={publicUrl}
      />
    </div>
  );
}
